import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const DivisionImage = ({ images, alt, testId }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 4000);
    return () => clearInterval(t);
  }, [images.length]);
  return (
    <>
      {images.map((src, i) => (
        <motion.img
          key={src}
          src={src}
          alt={alt}
          loading="lazy"
          initial={false}
          animate={{ opacity: i === idx ? 1 : 0 }}
          transition={{ duration: 0.9, ease: "easeInOut" }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ))}
      {images.length > 1 && (
        <div className="absolute bottom-4 right-4 flex gap-1.5 z-10">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`w-5 h-1 transition-colors duration-300 ${i === idx ? "bg-neon" : "bg-white/30"}`}
              data-testid={`${testId}-slide-dot-${i}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </>
  );
};

const DIVISIONS = [
  {
    num: "01",
    title: "Healthcare",
    subtitle: "Medical Equipment & Instrumentation",
    description:
      "Manufacturing, supply and installation of medical equipment and instrumentation for hospitals, medical colleges and healthcare institutions.",
    tags: ["Manufacturing", "Supply & Installation", "Instrumentation"],
    image: "/assets/medical-showcase.jpg",
    images: [
      "/assets/medical-showcase.jpg",
      "/assets/medical-operating-theatre.jpg",
      "/assets/medical-equipment-bay.jpg",
      "/assets/medical-laboratory.jpg",
    ],
    span: "md:col-span-7",
  },
  {
    num: "02",
    title: "Renewable Energy",
    subtitle: "Solar Power Plants",
    description:
      "Utility-scale solar power plants and solar charging infrastructure — engineered, installed and commissioned for a sustainable future.",
    tags: ["Utility-Scale Plants", "EV Charging Infrastructure", "Commissioning"],
    image: "/assets/solar-plant-sunset.jpg",
    images: ["/assets/solar-plant-sunset.jpg", "/assets/ev-charging.png", "/assets/solar-plant.jpg"],
    span: "md:col-span-5",
  },
  {
    num: "03",
    title: "Clean Energy",
    subtitle: "Green Hydrogen Plants",
    description:
      "Next-generation green hydrogen production plants powered by renewable energy — driving decarbonisation and energy independence.",
    tags: ["Electrolyser Plants", "Renewable-Powered", "Decarbonisation"],
    image: "/assets/hydrogen-plant.jpg",
    images: ["/assets/hydrogen-plant.jpg", "/assets/hydrogen-tanks.jpg"],
    span: "md:col-span-5",
  },
  {
    num: "04",
    title: "EPC",
    subtitle: "Engineering, Procurement & Construction",
    description:
      "End-to-end EPC delivery covering design engineering, procurement and turnkey construction with rigorous project management.",
    tags: ["Design Engineering", "Turnkey Delivery", "Project Management"],
    image: "/assets/epc-photo.jpg",
    images: ["/assets/epc-photo.jpg", "/assets/office-building.jpg"],
    span: "md:col-span-7",
  },
];

export default function Divisions() {
  return (
    <section id="divisions" className="py-28 lg:py-40" data-testid="divisions-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="font-mono-accent text-xs tracking-[0.35em] uppercase text-neon mb-6"
        >
          Business Divisions
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight max-w-3xl mb-20"
          data-testid="divisions-heading"
        >
          Four sectors. <span className="text-outline">One standard.</span>
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {DIVISIONS.map((d, i) => (
            <motion.article
              key={d.num}
              initial={{ opacity: 0, y: 48 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: (i % 2) * 0.15, ease: [0.16, 1, 0.3, 1] }}
              className={`group card-glow img-zoom border border-white/10 bg-navy-800/40 ${d.span}`}
              data-testid={`division-card-${d.num}`}
            >
              <div className="relative h-56 lg:h-64 overflow-hidden">
                <DivisionImage images={d.images || [d.image]} alt={d.subtitle} testId={`division-${d.num}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" />
                <span className="absolute top-5 left-5 font-mono-accent text-xs tracking-[0.3em] text-neon bg-navy/70 px-3 py-1.5 border border-neon/20">
                  {d.num}
                </span>
                <ArrowUpRight className="absolute top-5 right-5 w-5 h-5 text-slate-400 opacity-0 group-hover:opacity-100 group-hover:text-neon transition-all duration-300" />
              </div>
              <div className="p-7 lg:p-9">
                <h3 className="font-display text-2xl lg:text-3xl font-bold uppercase tracking-tight">{d.title}</h3>
                <p className="font-mono-accent text-[11px] tracking-[0.2em] uppercase text-electric mt-2">{d.subtitle}</p>
                <p className="text-sm text-slate-400 leading-relaxed mt-5">{d.description}</p>
                <div className="flex flex-wrap gap-2 mt-6">
                  {d.tags.map((tag) => (
                    <span
                      key={tag}
                      className="font-mono-accent text-[10px] tracking-[0.15em] uppercase text-slate-400 border border-white/10 px-3 py-1.5 group-hover:border-electric/40 transition-colors duration-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
